'use client';

import { useState, useCallback } from 'react';
import { useParams } from 'next/navigation';
import { useSuspenseQuery, useLazyQuery } from '@apollo/client/react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import EventList from '@/components/event-list';
import MaterialLinkButton from '@/components/material-link-button';
import SectionHeader from '@/components/section-header';
import StatusScheduleBanner from '@/components/event-series/status-schedule-banner';
import NoUpcomingEvents from '@/components/event-series/no-upcoming-events';
import Markdown from '@/components/markdown';
import HostSummaries from '@/components/host-summaries';
import { eventSeriesQuery, eventSeriesPastQuery } from '@/apollo/event-series';
import { extractId, eventSeriesPath } from '@/utils/slugify';

const EventSeriesDetailPageClient = () => {
  const { id: rawId } = useParams();
  const id = extractId(rawId as string);
  const { data } = useSuspenseQuery(eventSeriesQuery, {
    variables: { id },
  });
  const eventSeries = data.eventSeries;

  const [view, setView] = useState<'upcoming' | 'past'>('upcoming');
  const [loadPastEvents, { data: pastData, called: pastCalled }] = useLazyQuery(eventSeriesPastQuery);

  const handleViewChange = useCallback((_e: React.SyntheticEvent, newView: 'upcoming' | 'past' | null) => {
    if (!newView) return;
    setView(newView);
    if (newView === 'past' && !pastCalled) {
      loadPastEvents({ variables: { id } });
    }
  }, [pastCalled, loadPastEvents, id]);

  const pastEvents = pastData?.eventSeries?.pastEvents;

  const lastPastEvent = eventSeries.pastEvents?.[0];

  return (
    <>
      <StatusScheduleBanner eventSeries={eventSeries} />

      {eventSeries.description && (
        <div>
          <Markdown>{eventSeries.description}</Markdown>
        </div>
      )}

      <SectionHeader
        action={
          <ToggleButtonGroup value={view} exclusive size="small" onChange={handleViewChange}>
            <ToggleButton value="upcoming">
              Upcoming ({eventSeries.upcomingEventsCount})
            </ToggleButton>
            <ToggleButton value="past">
              Past ({eventSeries.pastEventsCount})
            </ToggleButton>
          </ToggleButtonGroup>
        }
      >
        {view === 'upcoming' ? 'Upcoming' : 'Past'}
      </SectionHeader>

      {view === 'upcoming' ? (
        eventSeries.upcomingEvents?.length > 0 ? (
          <>
            <EventList events={eventSeries.upcomingEvents} fullDate />
            {eventSeries.permissions?.editable && (
              <MaterialLinkButton
                href={`${eventSeriesPath(eventSeries.id, eventSeries.name)}/schedule`}
                variant="contained"
                color="primary"
                sx={{ mt: 1 }}
              >
                Schedule events
              </MaterialLinkButton>
            )}
          </>
        ) : (
          <NoUpcomingEvents eventSeries={eventSeries} lastEvent={lastPastEvent} />
        )
      ) : (
        pastEvents && pastEvents.length > 0 ? (
          <EventList events={pastEvents} fullDate />
        ) : (
          <div>No past events</div>
        )
      )}

      <HostSummaries event={eventSeries} />
    </>
  );
};

export default EventSeriesDetailPageClient;
